/**
 * Change Management Dashboard
 * Industry-standard ITIL-compliant Change Management System
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight2, 
  Add, 
  Clock, 
  TickCircle, 
  CloseCircle, 
  Timer1, 
  Activity,
  DocumentText,
  Warning2,
  Chart,
  ArrowUp,
  ArrowDown,
  Refresh,
  People,
  Setting4
} from 'iconsax-react';
import { useRBAC } from '../../contexts/RBACContext';
import { 
  getChangeStatistics, 
  getChanges, 
  getPendingApprovals,
  ChangeRequest, 
  ChangeStatistics,
  getStatusColor,
  getStatusLabel,
  getRiskColor,
  getCategoryIcon,
  getTypeColor
} from '../../services/changeManagementApi';
import toast from 'react-hot-toast';

const ChangesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { canAccess } = useRBAC();
  const [statistics, setStatistics] = useState<ChangeStatistics | null>(null);
  const [recentChanges, setRecentChanges] = useState<ChangeRequest[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [stats, recent, pending] = await Promise.all([
        getChangeStatistics(),
        getChanges({ limit: 5, sort_by: '-created_at' }),
        getPendingApprovals()
      ]);
      setStatistics(stats);
      setRecentChanges(recent.changes);
      setPendingApprovals(pending);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    trend?: { value: number; positive: boolean };
    onClick?: () => void;
  }> = ({ title, value, icon, color, trend, onClick }) => (
    <div 
      className={`bg-background-card rounded-xl p-6 border border-border-dark hover:border-primary/50 transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend.positive ? 'text-green-400' : 'text-red-400'}`}>
            {trend.positive ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            <span className="ml-1">{trend.value}%</span>
          </div>
        )}
      </div>
      <h3 className="text-gray-400 text-sm font-medium">{title}</h3>
      <p className="text-2xl font-bold text-white mt-1">{value}</p>
    </div>
  );

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(status as any)}`}>
      {getStatusLabel(status as any)}
    </span>
  );

  const RiskBadge: React.FC<{ risk: string }> = ({ risk }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getRiskColor(risk as any)}`}>
      {risk.charAt(0).toUpperCase() + risk.slice(1)}
    </span>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Change Management</h1>
          <p className="text-gray-400 mt-1">Track and manage all organizational changes</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchDashboardData}
            className="p-2 rounded-lg bg-background-card border border-border-dark hover:bg-background-hover transition-colors"
            title="Refresh"
          >
            <Refresh size={20} color="#9CA3AF" />
          </button>
          {canAccess('changes_manage') && (
            <button
              onClick={() => navigate('/change-management/teams')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 rounded-lg text-white hover:bg-green-700 transition-colors"
            >
              <People size={20} color="#FFFFFF" />
              <span>Manage Teams</span>
            </button>
          )}
          <button
            onClick={() => navigate('/changes/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary rounded-lg text-white hover:bg-primary/80 transition-colors"
          >
            <Add size={20} color="#FFFFFF" />
            <span>New Change Request</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Changes"
          value={statistics?.total_changes || 0}
          icon={<DocumentText size={24} color="#60A5FA" variant="Bulk" />}
          color="bg-blue-500/20"
          onClick={() => navigate('/changes')}
        />
        <StatCard
          title="Pending Approvals"
          value={statistics?.pending_approvals || 0}
          icon={<Timer1 size={24} color="#FBBF24" variant="Bulk" />}
          color="bg-yellow-500/20"
          onClick={() => navigate('/changes?status=submitted,under_review')}
        />
        <StatCard
          title="Success Rate"
          value={`${statistics?.success_rate || 0}%`}
          icon={<TickCircle size={24} color="#34D399" variant="Bulk" />}
          color="bg-green-500/20"
          trend={
            statistics && 
            typeof statistics.success_rate_trend === 'number' && 
            statistics.success_rate_trend !== 0 &&
            !isNaN(statistics.success_rate_trend)
              ? {
                  value: Math.abs(Number(statistics.success_rate_trend.toFixed(1))),
                  positive: statistics.success_rate_trend >= 0,
                }
              : undefined
          }
        />
        <StatCard
          title="This Week"
          value={statistics?.recent_changes || 0}
          icon={<Activity size={24} color="#A78BFA" variant="Bulk" />}
          color="bg-purple-500/20"
        />
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Status */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Chart size={20} color="#426CFF" />
            Changes by Status
          </h2>
          <div className="space-y-3">
            {statistics?.by_status && Object.entries(statistics.by_status).map(([status, count]) => (
              count > 0 && (
                <div 
                  key={status}
                  className="flex items-center justify-between p-3 bg-background-hover rounded-lg cursor-pointer hover:bg-background-card transition-colors"
                  onClick={() => navigate(`/changes?status=${status}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(status as any)}`}></div>
                    <span className="text-gray-300">{getStatusLabel(status as any)}</span>
                  </div>
                  <span className="text-white font-semibold">{count}</span>
                </div>
              )
            ))}
          </div>
        </div>

        {/* By Category */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <DocumentText size={20} color="#426CFF" />
            Changes by Category
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {statistics?.by_category && Object.entries(statistics.by_category).map(([category, count]) => (
              count > 0 && (
                <div 
                  key={category}
                  className="flex items-center justify-between p-3 bg-background-hover rounded-lg cursor-pointer hover:bg-background-card transition-colors"
                  onClick={() => navigate(`/changes?category=${category}`)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getCategoryIcon(category as any)}</span>
                    <span className="text-gray-300 capitalize text-sm">{category}</span>
                  </div>
                  <span className="text-white font-semibold">{count}</span>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      {/* Risk & Type Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Risk Level */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Warning2 size={20} color="#FB923C" />
            Risk Distribution
          </h2>
          <div className="flex items-center gap-4">
            {statistics?.by_risk_level && Object.entries(statistics.by_risk_level).map(([risk, count]) => (
              <div 
                key={risk}
                className="flex-1 text-center p-4 bg-background-hover rounded-lg cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate(`/changes?risk_level=${risk}`)}
              >
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${getRiskColor(risk as any)}/20`}>
                  <span className={`text-lg font-bold ${getRiskColor(risk as any).replace('bg-', 'text-')}`}>
                    {count}
                  </span>
                </div>
                <span className="text-gray-400 text-sm capitalize">{risk}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Type */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity size={20} color="#60A5FA" />
            Change Types
          </h2>
          <div className="flex items-center gap-4">
            {statistics?.by_type && Object.entries(statistics.by_type).map(([type, count]) => (
              <div 
                key={type}
                className="flex-1 text-center p-4 bg-background-hover rounded-lg cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate(`/changes?change_type=${type}`)}
              >
                <div className={`inline-block px-3 py-1 rounded-full mb-2 ${getTypeColor(type as any)}`}>
                  <span className="text-white text-sm capitalize">{type}</span>
                </div>
                <p className="text-2xl font-bold text-white">{count}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Changes & Pending Approvals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Changes */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock size={20} color="#426CFF" />
              Recent Changes
            </h2>
            <button 
              onClick={() => navigate('/changes')}
              className="text-primary text-sm hover:underline flex items-center gap-1"
            >
              View All <ArrowRight2 size={16} />
            </button>
          </div>
          <div className="space-y-3">
            {recentChanges.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No changes yet</p>
            ) : (
              recentChanges.map((change) => (
                <div 
                  key={change.id}
                  className="p-4 bg-background-hover rounded-lg cursor-pointer hover:bg-background-card transition-colors border border-transparent hover:border-primary/30"
                  onClick={() => navigate(`/changes/${change.change_id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-primary text-sm font-mono">{change.change_id}</span>
                        <StatusBadge status={change.status} />
                      </div>
                      <h3 className="text-white font-medium truncate">{change.title}</h3>
                      <p className="text-gray-400 text-sm mt-1">
                        {change.team_name} • {change.requester_name || 'Unknown'}
                      </p>
                    </div>
                    <RiskBadge risk={change.risk_level} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="bg-background-card rounded-xl p-6 border border-border-dark">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Timer1 size={20} color="#FBBF24" />
              Pending Approvals
            </h2>
            <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full text-sm">
              {pendingApprovals.length} pending
            </span>
          </div>
          <div className="space-y-3">
            {pendingApprovals.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No pending approvals</p>
            ) : (
              pendingApprovals.slice(0, 5).map((change) => (
                <div 
                  key={change.id}
                  className="p-4 bg-background-hover rounded-lg cursor-pointer hover:bg-background-card transition-colors border border-yellow-500/20"
                  onClick={() => navigate(`/changes/${change.change_id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-primary text-sm font-mono">{change.change_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeColor(change.change_type)}`}>
                          {change.change_type}
                        </span>
                      </div>
                      <h3 className="text-white font-medium truncate">{change.title}</h3>
                      <p className="text-gray-400 text-sm mt-1">
                        Requested by {change.requester_name || 'Unknown'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      <RiskBadge risk={change.risk_level} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-background-card rounded-xl p-6 border border-border-dark">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <button
            onClick={() => navigate('/changes/new')}
            className="p-4 bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors text-center"
          >
            <Add size={32} color="#426CFF" style={{ margin: '0 auto 8px' }} />
            <span className="text-white text-sm">New Change</span>
          </button>
          <button
            onClick={() => navigate('/changes?status=submitted,under_review')}
            className="p-4 bg-yellow-500/10 rounded-lg hover:bg-yellow-500/20 transition-colors text-center"
          >
            <Timer1 size={32} color="#FBBF24" style={{ margin: '0 auto 8px' }} />
            <span className="text-white text-sm">Review Pending</span>
          </button>
          <button
            onClick={() => navigate('/changes?status=in_progress')}
            className="p-4 bg-orange-500/10 rounded-lg hover:bg-orange-500/20 transition-colors text-center"
          >
            <Activity size={32} color="#FB923C" style={{ margin: '0 auto 8px' }} />
            <span className="text-white text-sm">In Progress</span>
          </button>
          {canAccess('changes_manage') && (
            <button
              onClick={() => navigate('/change-management/teams')}
              className="p-4 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-colors text-center"
            >
              <People size={32} color="#34D399" style={{ margin: '0 auto 8px' }} />
              <span className="text-white text-sm">Manage Teams</span>
            </button>
          )}
          <button
            onClick={() => navigate('/change-templates')}
            className="p-4 bg-purple-500/10 rounded-lg hover:bg-purple-500/20 transition-colors text-center"
          >
            <DocumentText size={32} color="#A78BFA" style={{ margin: '0 auto 8px' }} />
            <span className="text-white text-sm">Templates</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangesDashboard;
